import { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from './ui/command';
import { ChevronDown, Check } from 'lucide-react';

export const AgentAvatar = ({ agent, size = 'h-6 w-6' }) => {
  if (agent?.profile_picture) {
    return <img src={agent.profile_picture} alt="" className={`${size} rounded-full object-cover`} />;
  }
  const initials = (agent?.name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  return (
    <span className={`${size} flex items-center justify-center rounded-full bg-[#E9EDF7] text-[10px] font-bold text-[#2B3674]`}>
      {initials}
    </span>
  );
};

/**
 * Searchable agent dropdown (self + downline / admin = everyone).
 * Renders nothing when the user can only see themselves.
 */
export default function AgentPicker({ agents, selectedAgentId, onChange, testIdPrefix = 'heatmap' }) {
  const [open, setOpen] = useState(false);

  const selectedAgent = useMemo(() => {
    if (selectedAgentId) return agents.find((a) => a.id === selectedAgentId) || null;
    return agents.find((a) => a.is_self) || null;
  }, [agents, selectedAgentId]);

  const viewingSelf = !selectedAgentId || selectedAgent?.is_self;

  if (!Array.isArray(agents) || agents.length <= 1) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-agent-picker`}
          className="inline-flex h-9 max-w-[210px] items-center gap-2 rounded-md border border-[#E9EDF7] bg-white px-2.5 text-xs font-semibold text-[#2B3674] hover:bg-[#F4F7FE]"
        >
          <AgentAvatar agent={selectedAgent} size="h-5 w-5" />
          <span className="truncate">
            {viewingSelf ? 'My Activity' : selectedAgent?.name || 'Select agent'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[#A3AED0]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Search agents..." data-testid={`${testIdPrefix}-agent-search`} />
          <CommandList className="max-h-[260px]">
            <CommandEmpty>No agents found.</CommandEmpty>
            {agents.map((a) => {
              const isActive = selectedAgent?.id === a.id;
              return (
                <CommandItem
                  key={a.id}
                  value={a.name}
                  data-testid={`${testIdPrefix}-agent-option-${a.id}`}
                  onSelect={() => {
                    onChange(a.is_self ? null : a.id);
                    setOpen(false);
                  }}
                  className="gap-2"
                >
                  <AgentAvatar agent={a} />
                  <span className="flex-1 truncate text-[13px] font-medium text-[#2B3674]">
                    {a.name}
                    {a.is_self && <span className="ml-1.5 text-[10px] font-bold text-[#4318FF]">You</span>}
                  </span>
                  {isActive && <Check className="h-4 w-4 text-[#4318FF]" />}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
